import type { FieldDefinition } from "@estelle/domain";

/**
 * The fields All Contacts offers to the filter builder, the column chooser and
 * the sort control (#493).
 *
 * All Contacts lists two kinds of row in one table — the contacts the team keeps
 * by hand and Shopify's customers — so the vocabulary is the one they share, and
 * the columns are their own where one kind has nothing to say: a customer has no
 * company, GSTIN, type or archive flag, and shows a dash there; a contact has no
 * order figures, so none are offered. Vendors and Suppliers are lists of
 * contacts only and keep `CONTACT_FIELDS`.
 *
 * Every name here is projected by both adapters — the API's SQL field map
 * (`apps/api/src/routes/directory.ts`) and the fixture adapter's projection —
 * because a field that is offered and matches nothing reads as missing data.
 *
 * GSTIN, Type and Archived are a contact's own and a dash on every customer, so
 * they are left out of the starting columns and offered in the chooser
 * (`defaultHidden`): the two kinds together are nine columns wide with them
 * out, and twelve with them in. A stored column preference still wins.
 *
 * `kind` is the column and the filter that tell the two apart. `city` and
 * `state` are a contact's primary address and a customer's latest shipping
 * address. `phone` and `email` search every number and address a contact has.
 *
 * `phone` and `email` are not dropped for someone without
 * `customers.view_contact` (`CUSTOMER_FIELDS` does): a contact's own are theirs
 * to see, and a customer's are simply not sent — the server leaves them out of
 * the query, so the customer shows a dash and no filter, sort or search can ask
 * whether one exists.
 */
export const DIRECTORY_FIELDS: FieldDefinition[] = [
  { name: "displayName", label: "Name", type: "text", sortable: true },
  /*
    Kind and Company start hidden (#637): most rows are customers, for whom
    Company is empty and Kind always says Customer. Kind stays a filter, and
    the Name cell says when a row is a hand-kept contact.
  */
  {
    name: "kind",
    label: "Kind",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "contact", label: "Contact" },
      { value: "customer", label: "Customer" },
    ],
  },
  {
    name: "companyName",
    label: "Company",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  { name: "phone", label: "Phone", type: "text" },
  { name: "email", label: "Email", type: "text", defaultHidden: true },
  { name: "city", label: "City", type: "text", sortable: true },
  { name: "state", label: "State", type: "text", sortable: true },
  /*
    A customer's figures, as Customers shows them (#637), so a repeat buyer
    is told from a one-time one without opening either. A contact has none and
    shows a dash; a filter on them matches customers only.
  */
  { name: "ordersCount", label: "Orders", type: "number", sortable: true },
  { name: "totalSpent", label: "Spent", type: "money", sortable: true },
  { name: "lastOrderAt", label: "Last order", type: "date", sortable: true },
  { name: "gstin", label: "GSTIN", type: "text", defaultHidden: true },
  { name: "tags", label: "Tags", type: "tag" },
  { name: "typeIds", label: "Type", type: "tag", defaultHidden: true },
  {
    name: "isArchived",
    label: "Archived",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "createdAt",
    label: "Created",
    type: "date",
    sortable: true,
    defaultHidden: true,
  },
];
